/**
 * Feb 2026 — UserTimesheetHover
 * ------------------------------------------------------------------
 * Compact hover preview of a user's timesheet across the Daily
 * Completion Report's date range. Shows the tasks they logged effort
 * against but have NOT completed — the "close but not done" pile a
 * PM wants to nudge.
 *
 * Wraps any trigger element like `PackageHoverPreview` does. Lazy-
 * fetches on first hover to keep the report snappy for workspaces
 * with hundreds of members.
 */
import { useState } from "react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Loader2, Clock3, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

const PRIORITY_TONE = {
  urgent: "text-red-600", high: "text-orange-600",
  medium: "text-blue-600", low: "text-emerald-600",
};

export default function UserTimesheetHover({ wsId, userId, dateFrom, dateTo, children, disabled = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  if (disabled || !userId) return <>{children}</>;

  const onOpenChange = async (open) => {
    if (!open || data || loading) return;
    setLoading(true); setErr(null);
    try {
      const { data: resp } = await api.get(
        `/workspaces/${wsId}/reports/daily-completion/user-timesheet`,
        { params: { user_id: userId, date: dateFrom, date_to: dateTo }, timeout: 12000 });
      setData(resp);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to load timesheet");
    } finally { setLoading(false); }
  };

  return (
    <HoverCard openDelay={320} closeDelay={120} onOpenChange={onOpenChange}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent side="right" align="start" collisionPadding={16} avoidCollisions
                        className="w-[360px] p-0 rounded-none overflow-hidden text-[10.5px]"
                        data-testid={`timesheet-hover-${userId}`}>
        <div className="max-h-[min(70vh,520px)] flex flex-col">
          <div className="px-2.5 py-1.5 border-b border-border bg-muted/30 flex items-center gap-2 shrink-0">
            <Clock3 className="w-3 h-3 text-primary shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-semibold leading-tight">Timesheet · {dateFrom}{dateTo !== dateFrom ? ` → ${dateTo}` : ""}</div>
              {data && (
                <div className="text-[9.5px] text-muted-foreground">
                  <strong className="font-mono">{data.total_hours}h</strong> across {data.tasks.length} task{data.tasks.length === 1 ? "" : "s"}
                  {data.not_completed_count > 0 && (
                    <span className="ml-1 text-amber-700">· {data.not_completed_count} not completed</span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-auto">
            {loading && (
              <div className="p-4 text-center text-muted-foreground flex items-center justify-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading…
              </div>
            )}
            {err && (
              <div className="p-3 text-[10px] text-destructive flex items-start gap-1.5">
                <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {err}
              </div>
            )}
            {data && data.tasks.length === 0 && !loading && (
              <div className="p-4 text-center text-[10px] text-muted-foreground italic">
                No time logged in this range.
              </div>
            )}
            {data && data.tasks.length > 0 && (
              <ul className="divide-y divide-border/60">
                {data.tasks.map((t) => {
                  const isDone = t.status === "done";
                  return (
                    <li key={t.task_id} className="px-2.5 py-1 grid grid-cols-[1fr_auto_auto] gap-1.5 items-center"
                        data-testid={`timesheet-hover-task-${t.task_id}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${isDone ? "bg-emerald-500" : t.status === "in_progress" ? "bg-blue-500" : "bg-slate-400"}`} />
                          <span className={`truncate font-medium ${PRIORITY_TONE[t.priority] || ""}`} title={t.task_title}>{t.task_title}</span>
                        </div>
                        <div className="text-[9px] text-muted-foreground truncate pl-3">
                          {[t.client_name, t.brand_name, t.project_name].filter(Boolean).join(" · ") || "—"}
                        </div>
                      </div>
                      <span className="font-mono text-[10px] font-semibold tabular-nums">{t.hours}h</span>
                      <span className={`text-[9px] font-medium ${isDone ? "text-emerald-700" : "text-amber-700"}`}>
                        {isDone ? "done" : t.status}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
